import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { FormSelectOption, MultiFormSelectOptions, multiFormSelectOptionWrapper } from '@stylehaven/components';
import { WebserviceData } from '../../../../external-webservice/entities/webservice-data.entity';
import { ActivityMapping, JdnWebserviceConfigJson } from './jdn-webservice-config.entity';

@Injectable()
export class JdnWebservice {
    private staticWebserviceData$: Observable<WebserviceData[]>;
    private webserviceConfig: JdnWebserviceConfigJson;

    private idProperty = 'projectId';
    private labelProperty = 'projectName';
    private activityProperty = 'activities';
    private activityTypeProperty = 'mainActivityName';
    private activityValueProperty = 'activityName';

    constructor(protected http: HttpClient) {}

    public initWebservice(config: JdnWebserviceConfigJson): void {
        this.webserviceConfig = config;

        this.staticWebserviceData$ = this.getWebserviceData(this.webserviceConfig.Url).pipe(shareReplay(1));
    }

    private getWebserviceData(request: string): Observable<WebserviceData[]> {
        const httpOptions: { headers: HttpHeaders } = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: 'Basic ' + btoa(`${this.webserviceConfig.Login}:${this.webserviceConfig.Password}`)
            })
        };

        return this.http
            .get<WebserviceData[]>(request, httpOptions)
            .pipe(catchError(error => this.handleWebserviceError(error)));
    }

    public globalSearch(term: string): Observable<MultiFormSelectOptions> {
        return this.staticWebserviceData$.pipe(
            map(results =>
                results.filter(result =>
                    (result[this.labelProperty] as string).toLowerCase().includes(term.toLowerCase())
                )
            ),
            map(results => results.slice(0, 51)), // To ensure performance of the list
            map(results => results.map(result => this.toFormSelectOption(result))),
            map(options => multiFormSelectOptionWrapper(options))
        );
    }

    public getOptionById(id: string): Observable<FormSelectOption> {
        return this.staticWebserviceData$.pipe(
            map(results => results.filter(result => result[this.idProperty].toString() === id)),
            map(results =>
                results.length > 0
                    ? this.toFormSelectOption(results[0], id)
                    : {
                          label: 'Project not found in database',
                          value: id
                      }
            )
        );
    }

    private toFormSelectOption(webserviceData: WebserviceData, fallbackLabel?: string): FormSelectOption {
        return {
            label: webserviceData[this.labelProperty] as string,
            value: (webserviceData[this.idProperty] as string) ?? fallbackLabel
        };
    }

    public getById(id: string): Observable<WebserviceData> {
        return this.staticWebserviceData$.pipe(
            map(results => results.filter(result => (result[this.idProperty] as string) === id)),
            map(results => results[0]),
            map(result => this.activityMapping(result))
        );
    }

    private activityMapping(project: WebserviceData): WebserviceData {
        const activityData: WebserviceData[] = Array.isArray(project[this.activityProperty])
            ? (project[this.activityProperty] as unknown as WebserviceData[])
            : [];

        const mappedActivityData: {
            [key: string]: string[];
        } = {};

        activityData.forEach(activity => {
            const activityType: string = activity[this.activityTypeProperty] as string;
            const activityValue: string = activity[this.activityValueProperty] as string;
            const mapping: ActivityMapping | undefined = this.webserviceConfig.ActivityMapping.find(
                activityMapping => activityMapping.TypeMatch === activityType
            );

            if (!mapping) {
                console.warn(`JdnWebservice:: No activity-mapping configured for type ${activityType}`);
                return;
            }

            if (!mappedActivityData[mapping.Field]) {
                mappedActivityData[mapping.Field] = [activityValue];
            } else {
                mappedActivityData[mapping.Field].push(activityValue);
            }
        });

        return {
            ...project,
            ...(mappedActivityData as unknown as WebserviceData)
        };
    }

    private handleWebserviceError(error: Error): Observable<WebserviceData[]> {
        console.error(`JdnWebservice:: Failed to retreive webservice data`, error);
        return of(JSON.parse('[]'));
    }
}
